<template>
  <div class="timeline">
    <div v-if="isLoading" class="timeline-loading">
      <span>Načítavam...</span>
    </div>
    <div v-else ref="timelineContainer" class="timeline__content">
      <div
        ref="timelineScroll"
        class="timeline-scroll"
        @mousemove="handleMouseMove"
        @mouseleave="handleMouseLeave"
        @mousedown="handleTimelineMouseDown"
      >
        <a-employee-timeblock-timeline-header
          :start-hour="startHour"
          :end-hour="endHour"
          :hour-width="hourWidth"
        />

        <div ref="timelineBar" class="timeline-bar">
          <!-- Non-working hours - before work -->
          <div
            class="non-working-hours"
            :style="{
              left: '0px',
              width: `${getHourPosition(employee.work_start)}px`,
            }"
          ></div>

          <!-- Non-working hours - after work -->
          <div
            class="non-working-hours"
            :style="{
              left: `${getHourPosition(employee.work_end)}px`,
              width: `${getHourWidth(24 - employee.work_end)}px`,
            }"
          ></div>

          <!-- Work hours background (transparent/white) -->
          <div
            class="work-hours-bar"
            :style="{
              left: `${getHourPosition(employee.work_start)}px`,
              width: `${getHourWidth(
                employee.work_end - employee.work_start,
              )}px`,
            }"
          ></div>

          <!-- Weekend background overlay -->
          <div v-if="isWeekend" class="weekend-overlay"></div>

          <!-- Existing scheduled blocks -->
          <div
            v-for="(task, index) in scheduledBlocks"
            :key="`task-${index}`"
            class="scheduled-block"
            :class="{
              'scheduled-block--blocker': [
                'blocker',
                'adhoc',
                'home_office',
                'vacation',
                'free_day',
                'office365_blocker',
              ].includes(task.type),
              'scheduled-block--concept': task.is_draft === true,
            }"
            :style="{
              left: `${getTimePosition(task._real_start)}px`,
              width: `${getTimeWidth(task._real_start, task._real_end)}px`,
            }"
            :title="`${task.subtask?.title || task.blocker?.type || 'Task'}`"
          ></div>

          <!-- Other timeblocks for same date -->
          <div
            v-for="(timeblock, index) in otherTimeblocksRendered"
            :key="`other-${index}`"
            class="other-timeblock"
            :style="{
              left: `${getTimePosition(timeblock._start)}px`,
              width: `${getTimeWidth(timeblock._start, timeblock._end)}px`,
            }"
            :title="`Other timeblock`"
          ></div>

          <!-- Hover preview block -->
          <div
            v-if="showPreview && previewBlock"
            class="preview-block"
            :class="{ 'is-stretching': dragState.isStretching }"
            :style="{
              left: `${getTimePosition(previewBlock.start)}px`,
              width: `${getTimeWidth(previewBlock.start, previewBlock.end)}px`,
            }"
          >
            <div class="preview-time-label">
              <div
                v-if="getTimeWidth(previewBlock.start, previewBlock.end) <= 58"
              >
                {{ formatTime(previewBlock.start) }}
              </div>
              <div v-else>
                {{ formatTime(previewBlock.start) }} -
                {{ formatTime(previewBlock.end) }}
              </div>
            </div>
          </div>

          <!-- Current timeblock (if valid) -->
          <div
            v-if="
              currentTimeblockRange.start &&
              currentTimeblockRange.end &&
              !dragState.isResizingCurrent
            "
            class="current-timeblock"
            :class="{
              'has-collision': collision.hasCollision,
              'outside-working-hours': isOutsideWorkingHours,
            }"
            :style="{
              left: `${getTimePosition(currentTimeblockRange.start)}px`,
              width: `${getTimeWidth(
                currentTimeblockRange.start,
                currentTimeblockRange.end,
              )}px`,
            }"
          >
            <!-- Duration display (centered in current timeblock) -->
            <div v-if="timeblockDuration > 0" class="timeblock-duration">
              {{ timeblockDuration.toFixed(2) }}h
            </div>

            <!-- Resize handles -->
            <div
              class="resize-handle resize-handle--left"
              @mousedown.stop="handleResizeMouseDown($event, 'left')"
            ></div>
            <div
              class="resize-handle resize-handle--right"
              @mousedown.stop="handleResizeMouseDown($event, 'right')"
            ></div>
          </div>

          <!-- Resizing current timeblock preview -->
          <div
            v-if="dragState.isResizingCurrent && resizedCurrentBlock"
            class="current-timeblock is-resizing"
            :class="{
              'has-collision': collision.hasCollision,
              'outside-working-hours': isOutsideWorkingHours,
            }"
            :style="{
              left: `${getTimePosition(resizedCurrentBlock.start)}px`,
              width: `${getTimeWidth(
                resizedCurrentBlock.start,
                resizedCurrentBlock.end,
              )}px`,
            }"
          >
            <div
              v-if="
                getTimeWidth(
                  resizedCurrentBlock.start,
                  resizedCurrentBlock.end,
                ) <= 58
              "
              class="preview-time-label"
            >
              {{ formatTime(resizedCurrentBlock.start) }}
            </div>
            <div v-else class="preview-time-label">
              {{ formatTime(resizedCurrentBlock.start) }} -
              {{ formatTime(resizedCurrentBlock.end) }}
            </div>
          </div>

          <!-- Collision segments -->
          <div
            v-for="(segment, index) in collisionSegments"
            :key="`collision-${index}`"
            class="collision-segment"
            :style="{
              left: `${getTimePosition(segment.start)}px`,
              width: `${getTimeWidth(segment.start, segment.end)}px`,
            }"
          ></div>

          <!-- Cursor line (snapped to 15-min intervals) -->
          <div
            v-if="showCursor && cursorPosition"
            class="timeline-cursor"
            :style="{
              left: `${getTimePosition(cursorPosition)}px`,
            }"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import moment from 'moment';
import AEmployeeTimeblockTimelineHeader from './a-employee-timeblock-timeline-header.vue';
import type { EmployeeFullData, TimeblockData } from './types';

interface TimelineTask {
  _real_start: Date;
  _real_end: Date;
  subtask?: { title: string };
  blocker?: { type: string };
  type?: string;
  is_draft?: boolean;
}

interface CollisionSegment {
  start: Date;
  end: Date;
}

interface Props {
  isLoading: boolean;
  employee: EmployeeFullData;
  date: Date | string | null;
  scheduledBlocks: TimelineTask[];
  otherTimeblocks?: TimeblockData[];
  currentTimeblockRange: {
    start: Date | null;
    end: Date | null;
  };
  collision: {
    hasCollision: boolean;
    startCollides: boolean;
    endCollides: boolean;
  };
  collisionSegments: CollisionSegment[];
  timeblockDuration: number;
  startHour: number;
  endHour: number;
  hourWidth: number;
}

const props = withDefaults(defineProps<Props>(), {
  otherTimeblocks: () => [],
});

const emit = defineEmits<{
  (
    e: 'timeblockUpdated',
    payload: { starts_at_time: string; ends_at_time: string },
  ): void;
}>();

const timelineContainer = ref<HTMLElement | null>(null);
const timelineScroll = ref<HTMLElement | null>(null);
const timelineBar = ref<HTMLElement | null>(null);

// Check if mobile device
const isMobile = ref(false);

// Interactive state
const showPreview = ref(false);
const previewBlock = ref<{ start: Date; end: Date } | null>(null);
const dragState = ref({
  isStretching: false,
  isResizingCurrent: false,
  resizeEdge: null as 'left' | 'right' | null,
  startX: 0,
  initialStart: null as Date | null,
  initialEnd: null as Date | null,
});
const resizedCurrentBlock = ref<{ start: Date; end: Date } | null>(null);

// Cursor line state
const showCursor = ref(false);
const cursorPosition = ref<Date | null>(null);

// Check if the current date is a weekend (Saturday or Sunday)
const isWeekend = computed(() => {
  // Use the explicit date prop first, fallback to currentTimeblockRange.start
  const date = props.date || props.currentTimeblockRange.start;
  if (!date) return false;

  const dayOfWeek = moment(date).day(); // 0 = Sunday, 6 = Saturday
  return dayOfWeek === 0 || dayOfWeek === 6;
});

// Check if the current timeblock is outside working hours
const isOutsideWorkingHours = computed(() => {
  if (!props.currentTimeblockRange.start || !props.currentTimeblockRange.end) {
    return false;
  }

  const startHour =
    props.currentTimeblockRange.start.getHours() +
    props.currentTimeblockRange.start.getMinutes() / 60;
  const endHour =
    props.currentTimeblockRange.end.getHours() +
    props.currentTimeblockRange.end.getMinutes() / 60;

  // Check if any part of the timeblock is outside working hours
  const isStartOutside =
    startHour < props.employee.work_start ||
    startHour >= props.employee.work_end;
  const isEndOutside =
    endHour <= props.employee.work_start || endHour > props.employee.work_end;

  return isStartOutside || isEndOutside;
});

// Convert other timeblocks to renderable format
const otherTimeblocksRendered = computed(() => {
  return props.otherTimeblocks
    .map((tb) => {
      if (!tb.date || !tb.starts_at_time || !tb.ends_at_time) return null;

      const [startHour, startMinute] = tb.starts_at_time.split(':').map(Number);
      const [endHour, endMinute] = tb.ends_at_time.split(':').map(Number);

      const start = moment(tb.date)
        .set({ hour: startHour, minute: startMinute, second: 0 })
        .toDate();
      const end = moment(tb.date)
        .set({ hour: endHour, minute: endMinute, second: 0 })
        .toDate();

      return { _start: start, _end: end };
    })
    .filter((tb) => tb !== null);
});

// Position calculations
const getHourPosition = (hour: number): number => {
  return (hour - props.startHour) * props.hourWidth;
};

const getHourWidth = (hours: number): number => {
  return hours * props.hourWidth;
};

const getTimePosition = (date: Date): number => {
  const hour = date.getHours();
  const minute = date.getMinutes();
  const totalHours = hour + minute / 60;
  return (totalHours - props.startHour) * props.hourWidth + 1;
};

const getTimeWidth = (start: Date, end: Date): number => {
  const startHour = start.getHours() + start.getMinutes() / 60;
  const endHour = end.getHours() + end.getMinutes() / 60;
  return (endHour - startHour) * props.hourWidth - 2;
};

// Time snapping utility - snap to 15 minute intervals
const snapToInterval = (date: Date, intervalMinutes = 15): Date => {
  const minutes = date.getMinutes();
  const snappedMinutes =
    Math.round(minutes / intervalMinutes) * intervalMinutes;
  return moment(date)
    .set({ minute: snappedMinutes, second: 0, millisecond: 0 })
    .toDate();
};

// Convert pixel position to time
const positionToTime = (pixelX: number): Date => {
  const hoursFromStart = pixelX / props.hourWidth;
  const totalHours = props.startHour + hoursFromStart;
  const hour = Math.floor(totalHours);
  const minute = (totalHours % 1) * 60;

  // Use current date from currentTimeblockRange or today
  const baseDate = props.currentTimeblockRange.start
    ? moment(props.currentTimeblockRange.start)
    : moment();

  return baseDate.set({ hour, minute, second: 0, millisecond: 0 }).toDate();
};

// Format time as HH:mm
const formatTime = (date: Date): string => {
  return moment(date).format('HH:mm');
};

// Check if mouse is over an existing block (x is already relative position with scroll)
// Note: This does NOT check the current timeblock because we want to allow
// clicking elsewhere to change the current timeblock to a new time
const isOverExistingBlock = (relativeX: number): boolean => {
  // Check if over any scheduled block (saved tasks from database)
  for (const task of props.scheduledBlocks) {
    const blockLeft = getTimePosition(task._real_start);
    const blockWidth = getTimeWidth(task._real_start, task._real_end);
    if (relativeX >= blockLeft && relativeX <= blockLeft + blockWidth) {
      return true;
    }
  }

  // Check if over any other timeblock (from other rows in the modal)
  for (const tb of otherTimeblocksRendered.value) {
    const blockLeft = getTimePosition(tb._start);
    const blockWidth = getTimeWidth(tb._start, tb._end);
    if (relativeX >= blockLeft && relativeX <= blockLeft + blockWidth) {
      return true;
    }
  }

  // Don't check current timeblock - allow changing it by clicking elsewhere
  return false;
};

// Mouse move handler - show preview
const handleMouseMove = (event: MouseEvent) => {
  // Disable hover preview on mobile
  if (isMobile.value) {
    return;
  }

  if (dragState.value.isStretching) {
    handleStretchMove(event);
    return;
  }

  if (dragState.value.isResizingCurrent) {
    handleResizeCurrentMove(event);
    return;
  }

  if (!timelineScroll.value || !timelineBar.value) return;

  // Get position relative to scroll container
  const rect = timelineScroll.value.getBoundingClientRect();
  const scrollLeft = timelineScroll.value.scrollLeft;

  // Calculate X position relative to timeline
  const x = event.clientX - rect.left + scrollLeft;

  // Always calculate cursor position (with 15-min snapping)
  const rawTime = positionToTime(x);
  const snappedTime = snapToInterval(rawTime);
  const cursorHour = snappedTime.getHours() + snappedTime.getMinutes() / 60;

  // Check if cursor is in working hours
  const isInWorkHours =
    cursorHour >= props.employee.work_start &&
    cursorHour < props.employee.work_end;

  // Only show cursor line if in working hours
  if (isInWorkHours) {
    cursorPosition.value = snappedTime;
    showCursor.value = true;
  } else {
    showCursor.value = false;
  }

  // Update cursor style based on work hours
  if (timelineScroll.value) {
    timelineScroll.value.style.cursor = isInWorkHours
      ? 'crosshair'
      : 'not-allowed';
  }

  // Allow preview to show even over existing blocks
  // This allows creating conflicting timeblocks

  // Convert to time and snap
  const snappedStart = snappedTime;

  // Calculate default end time (1 hour later)
  let snappedEnd = moment(snappedStart).add(1, 'hour').toDate();

  // Only show preview if start is within work hours
  if (isInWorkHours) {
    // If the end would exceed work hours, cap it at work_end
    const endHour = snappedEnd.getHours() + snappedEnd.getMinutes() / 60;

    if (endHour > props.employee.work_end) {
      // Create end time at exactly work_end
      snappedEnd = moment(snappedStart)
        .set({
          hour: Math.floor(props.employee.work_end),
          minute: (props.employee.work_end % 1) * 60,
          second: 0,
          millisecond: 0,
        })
        .toDate();
    }

    showPreview.value = true;
    previewBlock.value = {
      start: snappedStart,
      end: snappedEnd,
    };
  } else {
    showPreview.value = false;
  }
};

// Mouse leave handler
const handleMouseLeave = () => {
  if (!dragState.value.isStretching && !dragState.value.isResizingCurrent) {
    showPreview.value = false;
    showCursor.value = false;
    // Reset cursor style to default crosshair
    if (timelineScroll.value) {
      timelineScroll.value.style.cursor = 'crosshair';
    }
  }
};

// Handle mousedown on timeline (including header area)
const handleTimelineMouseDown = (event: MouseEvent) => {
  if (isMobile.value) return;
  if (!timelineScroll.value || !cursorPosition.value) return;

  // Get the target element
  const target = event.target as HTMLElement;

  // Only allow resize handles to work - don't interfere with them
  if (target.classList.contains('resize-handle')) {
    return;
  }

  // Allow clicking anywhere else, including over the current timeblock,
  // to create/redraw the block at the cursor position

  // Allow creating blocks even over existing blocks (scheduled or other timeblocks)
  // This enables creating conflicting timeblocks

  // Check if cursor is in work hours (cursor line must be visible)
  if (!showCursor.value || !showPreview.value || !previewBlock.value) {
    return;
  }

  // Start creating/stretching block at the snapped cursor position
  dragState.value.isStretching = true;
  dragState.value.startX = event.clientX;
  dragState.value.initialStart = cursorPosition.value;

  // Calculate initial end time (1 hour later, capped at work_end)
  let initialEnd = moment(cursorPosition.value).add(1, 'hour').toDate();
  const endHour = initialEnd.getHours() + initialEnd.getMinutes() / 60;
  if (endHour > props.employee.work_end) {
    initialEnd = moment(cursorPosition.value)
      .set({
        hour: Math.floor(props.employee.work_end),
        minute: (props.employee.work_end % 1) * 60,
        second: 0,
        millisecond: 0,
      })
      .toDate();
  }

  dragState.value.initialEnd = initialEnd;

  // Set initial preview block (already has 1 hour duration from preview)
  previewBlock.value = {
    start: cursorPosition.value,
    end: initialEnd,
  };

  event.preventDefault();
};

// Handle stretch move
const handleStretchMove = (event: MouseEvent) => {
  if (!dragState.value.initialStart || !timelineScroll.value) return;

  const rect = timelineScroll.value.getBoundingClientRect();
  const x = event.clientX - rect.left + timelineScroll.value.scrollLeft;

  const currentTime = positionToTime(x);
  let snappedTime = snapToInterval(currentTime);

  const initialStartTime = dragState.value.initialStart.getTime();
  const snappedTimeValue = snappedTime.getTime();

  if (snappedTimeValue >= initialStartTime) {
    // Stretching right (extending end)
    // Ensure minimum 15-minute block
    let endTime = snappedTime;
    if (snappedTimeValue === initialStartTime) {
      endTime = moment(snappedTime).add(15, 'minutes').toDate();
    }

    // Cap at work_end
    const endHour = endTime.getHours() + endTime.getMinutes() / 60;
    if (endHour > props.employee.work_end) {
      endTime = moment(endTime)
        .set({
          hour: Math.floor(props.employee.work_end),
          minute: (props.employee.work_end % 1) * 60,
          second: 0,
          millisecond: 0,
        })
        .toDate();
    }

    previewBlock.value = {
      start: dragState.value.initialStart,
      end: endTime,
    };

    // Update cursor to show at the actual block edge (end)
    cursorPosition.value = endTime;
    showCursor.value = true;
  } else {
    // Stretching left (extending start)
    // Cap at work_start
    const snappedStartHour =
      snappedTime.getHours() + snappedTime.getMinutes() / 60;
    if (snappedStartHour < props.employee.work_start) {
      snappedTime = moment(snappedTime)
        .set({
          hour: Math.floor(props.employee.work_start),
          minute: (props.employee.work_start % 1) * 60,
          second: 0,
          millisecond: 0,
        })
        .toDate();
    }

    previewBlock.value = {
      start: snappedTime,
      end: dragState.value.initialStart,
    };

    // Update cursor to show at the actual block edge (start)
    cursorPosition.value = snappedTime;
    showCursor.value = true;
  }
};

// Handle stretch end
const handleStretchEnd = () => {
  if (dragState.value.isStretching && previewBlock.value) {
    // Only emit if we have a valid block (start != end)
    const startTime = previewBlock.value.start.getTime();
    const endTime = previewBlock.value.end.getTime();

    if (endTime > startTime) {
      // Emit the new timeblock
      emit('timeblockUpdated', {
        starts_at_time: formatTime(previewBlock.value.start),
        ends_at_time: formatTime(previewBlock.value.end),
      });
    }
  }

  dragState.value.isStretching = false;
  dragState.value.initialStart = null;
  dragState.value.initialEnd = null;
  showPreview.value = false;
};

// Resize handle mouse down - start resizing
const handleResizeMouseDown = (event: MouseEvent, edge: 'left' | 'right') => {
  if (!props.currentTimeblockRange.start || !props.currentTimeblockRange.end)
    return;

  dragState.value.isResizingCurrent = true;
  dragState.value.resizeEdge = edge;
  dragState.value.startX = event.clientX;
  dragState.value.initialStart = props.currentTimeblockRange.start;
  dragState.value.initialEnd = props.currentTimeblockRange.end;

  resizedCurrentBlock.value = {
    start: props.currentTimeblockRange.start,
    end: props.currentTimeblockRange.end,
  };

  event.preventDefault();
};

// Handle resize current block move
const handleResizeCurrentMove = (event: MouseEvent) => {
  if (
    !dragState.value.initialStart ||
    !dragState.value.initialEnd ||
    !timelineScroll.value
  )
    return;

  const rect = timelineScroll.value.getBoundingClientRect();
  const x = event.clientX - rect.left + timelineScroll.value.scrollLeft;

  const currentTime = positionToTime(x);
  let snappedTime = snapToInterval(currentTime);

  if (dragState.value.resizeEdge === 'left') {
    // Resizing left edge (changing start time)
    // Cap at work_start
    const snappedStartHour =
      snappedTime.getHours() + snappedTime.getMinutes() / 60;
    if (snappedStartHour < props.employee.work_start) {
      snappedTime = moment(snappedTime)
        .set({
          hour: Math.floor(props.employee.work_start),
          minute: (props.employee.work_start % 1) * 60,
          second: 0,
          millisecond: 0,
        })
        .toDate();
    }

    // Make sure new start is before end
    if (snappedTime < dragState.value.initialEnd) {
      resizedCurrentBlock.value = {
        start: snappedTime,
        end: dragState.value.initialEnd,
      };
      // Update cursor to show at the actual block edge
      cursorPosition.value = snappedTime;
      showCursor.value = true;
    }
  } else if (dragState.value.resizeEdge === 'right') {
    // Resizing right edge (changing end time)
    // Cap at work_end
    const snappedEndHour =
      snappedTime.getHours() + snappedTime.getMinutes() / 60;
    if (snappedEndHour > props.employee.work_end) {
      snappedTime = moment(snappedTime)
        .set({
          hour: Math.floor(props.employee.work_end),
          minute: (props.employee.work_end % 1) * 60,
          second: 0,
          millisecond: 0,
        })
        .toDate();
    }

    // Make sure new end is after start
    if (snappedTime > dragState.value.initialStart) {
      resizedCurrentBlock.value = {
        start: dragState.value.initialStart,
        end: snappedTime,
      };
      // Update cursor to show at the actual block edge
      cursorPosition.value = snappedTime;
      showCursor.value = true;
    }
  }
};

// Handle resize current block end
const handleResizeCurrentEnd = () => {
  if (dragState.value.isResizingCurrent && resizedCurrentBlock.value) {
    // Emit the updated timeblock
    emit('timeblockUpdated', {
      starts_at_time: formatTime(resizedCurrentBlock.value.start),
      ends_at_time: formatTime(resizedCurrentBlock.value.end),
    });
  }

  dragState.value.isResizingCurrent = false;
  dragState.value.resizeEdge = null;
  dragState.value.initialStart = null;
  dragState.value.initialEnd = null;
  resizedCurrentBlock.value = null;
};

// Global mouse up handler
const handleGlobalMouseUp = () => {
  if (dragState.value.isStretching) {
    handleStretchEnd();
  } else if (dragState.value.isResizingCurrent) {
    handleResizeCurrentEnd();
  }
};

// Global mouse move handler
const handleGlobalMouseMove = (event: MouseEvent) => {
  if (dragState.value.isStretching) {
    handleStretchMove(event);
  } else if (dragState.value.isResizingCurrent) {
    handleResizeCurrentMove(event);
  }
};

// Setup global listeners
onMounted(() => {
  // Detect mobile device
  isMobile.value = window.innerWidth <= 768;

  // Listen for resize to update mobile status
  const handleResize = () => {
    isMobile.value = window.innerWidth <= 768;
  };
  window.addEventListener('resize', handleResize);

  document.addEventListener('mouseup', handleGlobalMouseUp);
  document.addEventListener('mousemove', handleGlobalMouseMove);
});

onUnmounted(() => {
  document.removeEventListener('mouseup', handleGlobalMouseUp);
  document.removeEventListener('mousemove', handleGlobalMouseMove);
});

// Expose refs for parent component
defineExpose({
  timelineScroll,
});
</script>

<style lang="sass" scoped>
.timeline
//   height: 98px

  &__content
    position: relative
    width: 100%

  &-loading
    height: 100%
    width: 100%
    display: flex
    align-items: center
    justify-content: center
    color: #666

.timeline-scroll
  overflow-x: auto
  overflow-y: hidden
  position: relative
  width: 100%
  scrollbar-width: none  // Firefox
  cursor: crosshair

  &::-webkit-scrollbar
    display: none  // Chrome, Safari, Opera

.timeline-bar
  position: relative
  height: 30px
  width: calc(24 * 60px)
  background: #fafafa
  border-radius: 4px

  @media (max-width: 768px)
    width: calc(24 * 50px)

.non-working-hours
  position: absolute
  top: 0
  height: 100%
  background: repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(224, 224, 224, 0.3) 10px, rgba(224, 224, 224, 0.3) 20px)
  z-index: 1
  pointer-events: none

.work-hours-bar
  position: absolute
  top: 0
  height: 100%
  background: transparent
  z-index: 1
  pointer-events: none

.weekend-overlay
  position: absolute
  top: 0
  left: 0
  width: 100%
  height: 100%
  background: rgba(255, 124, 124, 0.3) !important
  z-index: 1
  pointer-events: none
  border-radius: 4px

.scheduled-block
  position: absolute
  top: 5px
  height: 22px
  background: #7f39fb
  border: 1px solid #6a1bf7
  opacity: 0.6
  border-radius: 2px
  z-index: 2
  pointer-events: none

  &--blocker
    background: rgb(176, 0, 32)
    border: 1px solid rgb(156, 0, 28)
    opacity: 0.7

  &--concept
    background: repeating-linear-gradient(135deg, transparent, transparent 10px, rgba(127, 57, 251, 0.3) 10px, rgba(127, 57, 251, 0.3) 20px)

.other-timeblock
  position: absolute
  top: 5px
  height: 22px
  background: #FFA726
  opacity: 0.5
  border: 2px dashed #FF9800
  border-radius: 2px
  z-index: 2
  pointer-events: none

.preview-block
  position: absolute
  top: 5px
  height: 22px
  background: #4CAF50
  opacity: 0.6
  border: 2px dashed #388E3C
  border-radius: 2px
  z-index: 5
  cursor: crosshair
  transition: opacity 0.2s, box-shadow 0.2s
  display: flex
  align-items: center
  justify-content: center
  pointer-events: none

  &:hover
    opacity: 0.8
    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.4)

  &.is-stretching
    opacity: 0.8
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.6)
    cursor: ew-resize

.preview-time-label
  font-size: 11px
  font-weight: 600
  color: white
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5)
  white-space: nowrap
  pointer-events: none
  user-select: none

.current-timeblock
  position: absolute
  top: 5px
  height: 22px
  background: #2196F3
  opacity: 0.7
  border: 2px solid #1976D2
  border-radius: 2px
  z-index: 3
  transition: box-shadow 0.2s

  &.has-collision
    border-color: #ff5252

  &.outside-working-hours
    border-color: #ff0000

  &.is-resizing
    opacity: 0.8
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.6)
    z-index: 6

.resize-handle
  position: absolute
  top: 0
  width: 8px
  height: 100%
  cursor: ew-resize
  z-index: 4

  &:hover
    background: rgba(255, 255, 255, 0.3)

  &--left
    left: 0
    border-top-left-radius: 2px
    border-bottom-left-radius: 2px

  &--right
    right: 0
    border-top-right-radius: 2px
    border-bottom-right-radius: 2px

.collision-segment
  position: absolute
  top: 5px
  height: 22px
  background: #ff5252
  opacity: 0.6
  border-radius: 2px
  z-index: 4
  pointer-events: none

.timeline-cursor
  position: absolute
  top: 0
  bottom: 0
  width: 2px
  background: rgba(0, 0, 0, 0.3)
  z-index: 10
  pointer-events: none

.timeblock-duration
  position: absolute
  top: 50%
  left: 50%
  transform: translate(-50%, -50%)
  font-size: 14px
  font-weight: bold
  color: white
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3)
  pointer-events: none
  user-select: none

.timeline-loading
  height: 60px
  display: flex
  align-items: center
  justify-content: center
  font-size: 14px
  opacity: 0.5
</style>
